/**
 * paperSkin — the app's paper palette, in two modes, on BQC5's skin.
 *
 * SHARED, NOT OWNED. It was `subcontractors/subconTheme.ts` until BQC1's
 * planner board asked for the same look (Mikko, 2026-09-17: "i want that kind
 * of color here in BQC1 screen"). Two screens wearing one palette have to
 * share the FILE — a copied token set drifts on the first tweak, and the drift
 * shows as two screens that NEARLY match, which reads worse than two that
 * plainly do not. It lives outside both modules because it belongs to neither,
 * and because SUBCONTRACTORS is default-deny: a screen everybody can open must
 * not import anything from behind that guard.
 *
 * The tokens keep their `--sub-*` names. Renaming them would be a rename in
 * fifteen payroll files for no gain, and the prefix is now simply the name of
 * this palette rather than a claim about who uses it.
 *
 * What follows is the original header, and every word of it still holds.
 *
 * ── The SUBCONTRACTORS module\'s palette, in two modes, on BQC5\'s skin ──
 *
 * WHY IT LOOKS LIKE THE QC BENCH (Mikko, 2026-09-11): the payroll admin has
 * the same eyes as the inspectors, and BQC5 is the screen that was already
 * tuned for them — paper, one ink, one typeface, no glass. So this module
 * adopts that skin rather than keeping a look of its own: the palette below
 * is `stationSkin.ts`'s PAPER_INK and `lightMode.ts`'s LIGHT_VARS, and the
 * typeface is the bench's mono. A person walking from BQC5 to payroll reads
 * the same page.
 *
 * LIGHT IS THE DEFAULT, for the same reason BQC5 opens light: "cannot read
 * the dark one" is not a preference to discover. The dark canon is one click
 * away and remembered per browser.
 *
 * THE SIDEBAR IS NOT INCLUDED. The nav is the app's identity and is rendered
 * by the Shell, outside this module's root; the tokens are scoped to the root,
 * so light stops at the nav's edge without anyone remembering to leave it.
 *
 * WHY ITS OWN TOKENS. mfg-workcenters' theme is `var(--wc-*, <dark literal>)`
 * — a token never given a light value cannot have one. The `--sub-*` set is
 * defined COMPLETELY in both modes, and a test pins that.
 *
 * ONE TYPEFACE, the app's sans, at BQC5's sizes. Tabular figures keep the
 * amount columns aligned without a second face.
 *
 * A GREY TOUCH (Mikko, 2026-09-11): the structure is cool grey rather than
 * warm paper — a grey ground under white cards, a grey header band, grey
 * resting tabs and chips — with the khaki kept for the primary button, the
 * pager and a watch state. Grey does the bounding; khaki does the pointing.
 *
 * SEVERITY. On navy, severity = brightness; on paper, severity = depth. Still
 * no traffic lights — except the status pill, which borrows BQC5's four tones
 * (idle / part / done / bad) so a Paid payslip and a PASSED WIP read alike.
 *
 * Pure and I/O-free apart from one localStorage read; the hook lives in
 * useSubconMode.ts so this file can be tested without React.
 */
export type SubconMode = "dark" | "light";

/**
 * The typeface: the app's own (`--font`, DM Sans / Inter), which is what BQC5
 * renders in — its table setting is `system`, i.e. inherit. The first cut of
 * this skin set the module in the bench's optional mono; Mikko (2026-09-11)
 * pointed at BQC5's actual screen, which is the sans, at the bench's larger
 * sizes (header 14, body 14.5, rows 13px tall padding). `undefined` inherits.
 */
export const FONT: string | undefined = undefined;
/** For the few places that want a mono on purpose — ids, references. */
export const MONO = '"SF Mono", Monaco, Consolas, monospace';

/** BQC5's type scale (DEFAULT_TABLE_STYLE / DEFAULT_KPI_CARD_STYLE). */
export const SCALE = {
  body: 14,
  tableHeader: 13.5,
  tableHeaderSpacing: 0.6,
  tableBody: 14.5,
  rowPaddingY: 12,
  cellPaddingX: 16,
  status: 11,
  label: 11.5,
  input: 14,
  button: 13,
  tab: 15.5,
} as const;

/**
 * The CRM order tables' setting — larger than the bench's, and in the SAME
 * typeface as the masthead and the KPI tiles.
 *
 * WHY A SECOND SCALE RATHER THAN A BIGGER FIRST ONE. SCALE is BQC5's, worn by
 * every station screen and all of payroll; raising it would re-flow fifteen
 * dense operational tables to settle a question about two CRM screens. So this
 * is opt-in, named, and used by IG COM and TikTok Shop only.
 *
 * WHY IT DROPS THE MONO. The CRM order tables were set in `MONO` while the
 * masthead and the KPI figures above them were in the app's sans — two faces
 * stacked in one card, which is what Mikko was looking at (2026-09-20: "make
 * sure all font style here are copied t header and KPI font style"). Dropping
 * the mono costs nothing that was doing work: paperSkin's own header already
 * says the sans's TABULAR FIGURES are what keep the amount columns aligned,
 * and `NUMERIC` applies them. A mono was a second face buying an alignment we
 * already had.
 *
 * The header row gains on all three axes rather than just size — it was a
 * dim, small, widely-tracked line over a table whose body is now larger than
 * it was, and size alone would have left it reading as a caption.
 */
export const CRM_TABLE = {
  /** Up from SCALE.tableHeader's 13.5. */
  header: 14,
  /** Tighter than SCALE.tableHeaderSpacing: bigger uppercase needs less. */
  headerSpacing: 0.5,
  /** Vertical padding in the header band, up from 10. */
  headerPaddingY: 13,
  /** Up from SCALE.tableBody's 14.5 — the figure Mikko asked to enhance. */
  body: 15.5,
  /** Row breathing to match the larger body, up from SCALE.rowPaddingY's 12. */
  rowPaddingY: 13,
  /** The secondary line under a cell (a city, a SKU list), up from label's 11.5. */
  sub: 12.5,
} as const;

/** Every token the module's UI names. CSS custom properties so a mode change
 *  is one style object on the root. */
export const T = {
  bg: "var(--sub-bg, #081326)",
  panel: "var(--sub-panel, #0C1A33)",
  surface: "var(--sub-surface, #0F1C36)",
  surface2: "var(--sub-surface2, #152647)",
  /** The table header band — a tint of the page, not a grey. */
  band: "var(--sub-band, #13294B)",
  /** What BOUNDS something — a card, the data container. */
  edge: "var(--sub-edge, rgba(255,255,255,0.18))",
  /** What OUTLINES a control — inputs, chips, buttons. */
  border: "var(--sub-border, rgba(255,255,255,0.14))",
  /** What DIVIDES inside one box — table row rules. */
  borderSoft: "var(--sub-border-soft, rgba(255,255,255,0.06))",
  zebra: "var(--sub-zebra, rgba(255,255,255,0.025))",
  heading: "var(--sub-heading, #FFFFFF)",
  text: "var(--sub-text, #E2E8F0)",
  textDim: "var(--sub-text-dim, #9AA7BD)",
  textMuted: "var(--sub-text-muted, #8891A3)",
  textOff: "var(--sub-text-off, #5A6374)",
  /** Khaki as TEXT, at a weight legible on this ground. Rationed: a watch
   *  state and the one thing on a screen that wants attention. */
  accent: "var(--sub-accent, #C9A46A)",
  /** Khaki as a FILL — the primary button. Same in both modes. */
  accentFill: "var(--sub-accent-fill, #C9A46A)",
  onAccent: "var(--sub-on-accent, #081326)",
  rowHover: "var(--sub-row-hover, #152647)",
  icon: "var(--sub-icon, #8FA0BC)",
  /** The picked filter chip — BQC5's raised dark chip, in both modes. */
  selectedBg: "var(--sub-selected-bg, #434C5C)",
  selectedFg: "var(--sub-selected-fg, #FFFFFF)",
  selectedEdge: "var(--sub-selected-edge, #5B6779)",
  /** A FILLED ink pill — the top of a ladder. */
  strongBg: "var(--sub-strong-bg, #243B68)",
  strongFg: "var(--sub-strong-fg, #E8EEF9)",
  /** Severity ramp. */
  ok: "var(--sub-ok, #9AA7BD)",
  watch: "var(--sub-watch, #C9A46A)",
  danger: "var(--sub-danger, #E0C089)",
  risk: "var(--sub-risk, #F5EFE2)",
  watchWash: "var(--sub-watch-wash, rgba(201,164,106,0.08))",
  riskWash: "var(--sub-risk-wash, rgba(245,239,226,0.07))",
  /** The status pill's four tones — BQC5's, so PAID reads like PASSED. */
  pillIdle: "var(--sub-pill-idle, #9AA7BD)",
  pillPart: "var(--sub-pill-part, #E0B978)",
  pillDone: "var(--sub-pill-done, #3FBE93)",
  pillBad: "var(--sub-pill-bad, #F09A9A)",
  /** The pagination control: khaki fill, white figures (Mikko, 2026-09-11). */
  pagerBg: "var(--sub-pager-bg, #A6853F)",
  pagerFg: "var(--sub-pager-fg, #FFFFFF)",
  /** BQC5's folder-tab strip — StationTabs' two palettes, as tokens. */
  tabRaised: "var(--sub-tab-raised, linear-gradient(180deg, #5A6678 0%, #454E5D 48%, #333B48 100%))",
  tabResting: "var(--sub-tab-resting, linear-gradient(180deg, #1E2840 0%, #161F33 52%, #111827 100%))",
  tabEdge: "var(--sub-tab-edge, #5B6779)",
  tabRestEdge: "var(--sub-tab-rest-edge, #25314D)",
  tabRaisedFg: "var(--sub-tab-raised-fg, #FFFFFF)",
  tabBadgeBg: "var(--sub-tab-badge-bg, rgba(0,0,0,0.30))",
  /** Under a card. Nothing much on navy; a real cast on paper, where an edge
   *  alone reads as a wireframe. */
  shadow: "var(--sub-shadow, inset 0 1px 0 rgba(255,255,255,0.025))",
  shadowRaised: "var(--sub-shadow-raised, 0 18px 48px rgba(0,0,0,0.5))",
} as const;

/** The dark canon — navy surfaces, khaki attention, white emphasis. */
const DARK: Record<string, string> = {
  "--sub-bg": "#081326",
  "--sub-panel": "#0C1A33",
  "--sub-surface": "#0F1C36",
  "--sub-surface2": "#152647",
  "--sub-band": "#13294B",
  "--sub-edge": "rgba(255,255,255,0.18)",
  "--sub-border": "rgba(255,255,255,0.14)",
  "--sub-border-soft": "rgba(255,255,255,0.06)",
  "--sub-zebra": "rgba(255,255,255,0.025)",
  "--sub-heading": "#FFFFFF",
  "--sub-text": "#E2E8F0",
  "--sub-text-dim": "#9AA7BD",
  "--sub-text-muted": "#8891A3",
  "--sub-text-off": "#5A6374",
  "--sub-accent": "#C9A46A",
  "--sub-accent-fill": "#C9A46A",
  "--sub-on-accent": "#081326",
  "--sub-row-hover": "#152647",
  "--sub-icon": "#8FA0BC",
  "--sub-selected-bg": "#434C5C",
  "--sub-selected-fg": "#FFFFFF",
  "--sub-selected-edge": "#5B6779",
  "--sub-strong-bg": "#243B68",
  "--sub-strong-fg": "#E8EEF9",
  "--sub-ok": "#9AA7BD",
  "--sub-watch": "#C9A46A",
  "--sub-danger": "#E0C089",
  "--sub-risk": "#F5EFE2",
  "--sub-watch-wash": "rgba(201,164,106,0.08)",
  "--sub-risk-wash": "rgba(245,239,226,0.07)",
  "--sub-pill-idle": "#9AA7BD",
  "--sub-pill-part": "#E0B978",
  "--sub-pill-done": "#3FBE93",
  "--sub-pill-bad": "#F09A9A",
  "--sub-pager-bg": "#A6853F",
  "--sub-pager-fg": "#FFFFFF",
  "--sub-tab-raised": "linear-gradient(180deg, #5A6678 0%, #454E5D 48%, #333B48 100%)",
  "--sub-tab-resting": "linear-gradient(180deg, #1E2840 0%, #161F33 52%, #111827 100%)",
  "--sub-tab-edge": "#5B6779",
  "--sub-tab-rest-edge": "#25314D",
  "--sub-tab-raised-fg": "#FFFFFF",
  "--sub-tab-badge-bg": "rgba(0,0,0,0.30)",
  "--sub-shadow": "0 8px 28px -12px rgba(0,0,0,0.65)",
  "--sub-shadow-raised": "0 28px 64px -16px rgba(0,0,0,0.75)",
};

/**
 * Light — BQC5's ink and edges, on a grey ground.
 *
 * The ink, the edge weights, the accent and the pill tones are BQC5's. The
 * ground, band and resting surfaces are a cool grey rather than the bench's
 * warm paper — the grey touch Mikko asked for on top of the light theme. Three border weights, because an edge
 * does more work on paper than on navy: EDGE bounds a card, LINE outlines a
 * control, LINE_SOFT divides rows. The ink is one colour at four strengths.
 * The khaki darkens where it is text (#C9A46A is ~1.9:1 on white) and stays
 * the brand fill where it is a button.
 */
const LIGHT: Record<string, string> = {
  "--sub-bg": "#EEF0F3",
  "--sub-panel": "#E6E9ED",
  "--sub-surface": "#FFFFFF",
  "--sub-surface2": "#F3F4F6",
  "--sub-band": "#E4E7EB",
  "--sub-edge": "rgba(20,24,31,0.22)",
  "--sub-border": "rgba(20,24,31,0.16)",
  "--sub-border-soft": "rgba(20,24,31,0.10)",
  "--sub-zebra": "rgba(20,24,31,0.03)",
  "--sub-heading": "#14181F",
  "--sub-text": "#2A313C",
  "--sub-text-dim": "#5B6472",
  "--sub-text-muted": "#6B7482",
  // Held above 4:1 on white rather than the 2.5:1 a hint usually gets — the
  // person this mode exists for reads the hints.
  "--sub-text-off": "#6E7887",
  "--sub-accent": "#8A6A2F",
  "--sub-accent-fill": "#C9A46A",
  "--sub-on-accent": "#14181F",
  "--sub-row-hover": "#F3F4F6",
  "--sub-icon": "#5B6472",
  // The picked chip keeps its dark face on paper — depth is what "selected"
  // means here, the same call stationSkin makes for the station tabs.
  "--sub-selected-bg": "#2A313C",
  "--sub-selected-fg": "#FFFFFF",
  "--sub-selected-edge": "#14181F",
  "--sub-strong-bg": "#14181F",
  "--sub-strong-fg": "#FFFFFF",
  // Severity by depth.
  "--sub-ok": "#5B6472",
  "--sub-watch": "#9A6F12",
  "--sub-danger": "#B4571B",
  "--sub-risk": "#A32B22",
  "--sub-watch-wash": "#FDFAF3",
  "--sub-risk-wash": "#FBF3F1",
  // The pill tones, deepened for paper — the dark ones are tuned to glow.
  "--sub-pill-idle": "#5B6472",
  "--sub-pill-part": "#9A6F12",
  "--sub-pill-done": "#1E7A5A",
  "--sub-pill-bad": "#A32B22",
  // Khaki deep enough for white figures to clear AA on it (#8A6A2F ≈ 5:1).
  "--sub-pager-bg": "#8A6A2F",
  "--sub-pager-fg": "#FFFFFF",
  // StationTabs' paper palette: the selected tab is full paper standing
  // proud of a recessed strip; the resting tabs sit back, tinted.
  "--sub-tab-raised": "#FFFFFF",
  "--sub-tab-resting": "linear-gradient(180deg, #E3E6EA 0%, #D9DDE2 100%)",
  "--sub-tab-edge": "rgba(20,24,31,0.20)",
  "--sub-tab-rest-edge": "rgba(20,24,31,0.13)",
  "--sub-tab-raised-fg": "#14181F",
  "--sub-tab-badge-bg": "rgba(20,24,31,0.08)",
  "--sub-shadow": "0 1px 2px rgba(20,24,31,0.05), 0 10px 24px -16px rgba(20,24,31,0.26)",
  "--sub-shadow-raised": "0 24px 56px -16px rgba(20,24,31,0.30)",
};

/** The custom properties for a mode, to spread onto the module's root. */
export function themeVars(mode: SubconMode): Record<string, string> {
  return mode === "light" ? LIGHT : DARK;
}

/** Every token has a value in BOTH modes — a test pins it. */
export const TOKEN_NAMES: readonly string[] = Object.keys(DARK);

/** A card's geometry. The material is `.sub-card` in subconPaper.css. */
export const card: React.CSSProperties = {
  borderRadius: 14,
};

/** The page header, as BQC5 draws it: an ink eyebrow and a large quiet title. */
export const PAGE = {
  kicker: {
    fontSize: 11.5, fontWeight: 800, color: T.text, letterSpacing: "0.12em", textTransform: "uppercase",
  } as React.CSSProperties,
  title: {
    fontSize: 30, fontWeight: 700, letterSpacing: "-0.025em", color: T.heading,
  } as React.CSSProperties,
  subtitle: { fontSize: 13.5, color: T.textDim } as React.CSSProperties,
};

/** A KPI tile — BQC5's: a small-caps label, a large ink figure, a caption. */
export const KPI = {
  card: {
    borderRadius: 14,
    padding: "14px 16px",
    minWidth: 160,
  } as React.CSSProperties,
  label: {
    fontSize: 11, fontWeight: 700, color: T.textDim,
    letterSpacing: "0.08em", textTransform: "uppercase",
  } as React.CSSProperties,
  value: {
    fontSize: 28, fontWeight: 700, marginTop: 6, color: T.heading, letterSpacing: "-0.01em",
    fontVariantNumeric: "tabular-nums",
  } as React.CSSProperties,
  sub: { fontSize: 11.5, color: T.textDim, marginTop: 4 } as React.CSSProperties,
};

export const STORAGE_KEY = "isyn.subcontractors.mode";

/**
 * The stored preference, or the module default — LIGHT, like BQC5.
 *
 * Per browser: an accommodation belongs to a pair of eyes, not to an account.
 * Every access is wrapped: a browser with site data blocked throws on the
 * accessor itself, and a payroll screen must not fail over a colour.
 */
export function readMode(): SubconMode {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

export function writeMode(mode: SubconMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* A preference that cannot be remembered still applies for this session. */
  }
}
